import { Tooltip, TooltipContent, TooltipTrigger } from "@aircall/ds";
import { Info } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";

import { Feedbacks } from "@/blocks/Feedbacks";
import { Section } from "@/blocks/Section";
import { Link } from "@/components/Link/Link";

import { CsatDriver } from "../CsatDriver";
import {
  CsatRating,
  CsatScore,
  getCsatRating,
  getCsatRatingTextColor,
} from "../CsatScore";

import { SectionCsatProps } from "./SectionCsat.decl";
import { useSectionCsat } from "./SectionCsat.hooks";

const ratingLabelKey: Record<Exclude<CsatRating, "na">, string> = {
  high: "predicted_csat.rating.high",
  medium: "predicted_csat.rating.medium",
  low: "predicted_csat.rating.low",
};

export function SectionCsat({ score, drivers }: SectionCsatProps) {
  const { t } = useTranslation();

  const feedbackHandlers = useSectionCsat();

  const rating = getCsatRating(score);

  return (
    <Section data-test="predicted-csat-card">
      <div className="flex items-center gap-1">
        <span className="text-base font-semibold">
          {t("predicted_csat.title")}
        </span>
        <Tooltip>
          <TooltipTrigger render={<div data-test="predicted-csat-info" />}>
            <Info className="size-4" />
          </TooltipTrigger>
          <TooltipContent side="top" align="center">
            {t("predicted_csat.tooltip.content")}{" "}
            <Link href={t("predicted_csat.tooltip.href_url")} target="_blank">
              {t("predicted_csat.tooltip.href_title")}
            </Link>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1">
          <CsatScore value={score} />
          {rating !== "na" && (
            <span
              className={`text-sm font-semibold ${getCsatRatingTextColor(rating)}`}
            >
              {t(ratingLabelKey[rating])}
            </span>
          )}
        </div>
        <Feedbacks {...feedbackHandlers} />
      </div>
      {drivers.length > 0 && (
        <div className="flex flex-col gap-1">
          {drivers.map((driver, index) => (
            <CsatDriver
              key={`${driver.polarity}-${index}`}
              polarity={driver.polarity}
              label={driver.label}
            />
          ))}
        </div>
      )}
    </Section>
  );
}
